import { ErrorWithBreadcrumb } from '~/errors';

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  const cacheProviderAPI = useCacheProviderAPI();
  const DEFAULT_ACCOUNT_SETTINGS_MAX_AGE_MS = 5_000;

  // Using a Record to ensure that we have all the keys of AccountSettings in the object and no duplicates. The values dont matter, so we use null.
  const accountSettingsToFetch: Record<keyof AccountSettings, null> = {
    StatusChangeToCompletedRequiresNote: null,
    StatusChangeToPartiallyCompletedRequiresNote: null,
    CreateCalendarEventAccessLevel: null,
    ReadCalendarEventAccessLevel: null,
    LockedEventsCanBeMoved: null,
    LockedEventsCanBeUnlocked: null,
    PlanningDuration: null,
    UseInventory: null,
    ShowMaterialFavoriteList: null,
    MaterialPricesVisibilityInApp: null,
    LocationRequiredOnMaterialPage: null,
    MainLocationID: null,
    ShowOfferInApp: null,
    ShowNoQaButtonInApp: null,
    DebtorsFinancialAccountID: null,
    DefaultDebtorPaymentTermId: null,
    UseHourSpecificationAsDefault: null,
    IncludeEndDateForRentalCount: null,
    AllowMaterialMovementBelowZero: null,
    UseQACopySection: null,
    DefaultImageAppSize: null,
    AllowFitterAddQAToProjectInApp: null,
    PlanningWorkDayStartTime: null,
    PlanningWorkDayEndTime: null,
    NoteRequiredForHourRegistration: null,
    StartAndEndTimeRequiredForHourRegistration: null,
    WorksheetRequiresMaterialItems: null,
    WorksheetRequiresTimeRegistration: null,
    FitterAllowedToEditProjectDescription: null,
    FitterAllowedToEditWorksheetDescription: null,
    AllowEditProjectDescription: null,
    AllowEditWorkDescription: null,
    UseCostCodes: null,
    AllowCreateQALocation: null,
    ShowDebtorNoteOnWorksheet: null,
    DepartmentOnProjectRequired: null,
    CarrierOnProjectRequired: null,
    PurposeOnProjectRequired: null,
    IsDimensionDepartmentDisplayedOnApp: null,
    IsDimensionCarrierDisplayedOnApp: null,
    IsDimensionPurposeDisplayedOnApp: null,
    DefaultDepartmentOnProjectForApp: null,
    DefaultCarrierOnProjectForApp: null,
    DefaultPurposeOnProjectForApp: null,
    ShowSellerInApp: null,
    DefaultMobileProjectGroupID: null,
    FugeQA: null,
    ShowBaseSalaryInHourOverview: null,
    ShowMiscellaneousSalaryInHourOverview: null,
    ShowOvertimeSalaryInHourOverview: null,
    CanEditProjectNotesFromApp: null,
    CanDeleteProjectNotesFromApp: null,
    AllowLettersInProjectReference: null,
    UseBackgroundCheckViaPSecure: null,
    ShowBasketInApp: null,
    BrowserTitle: null,
    UsePush: null,
    AddInventoryOnLockedFinancialPeriodActionApp: null,
    MovePurchaseInvoiceOrInventoryOnLockedFinancialPeriodActionApp: null,
    CreateTimeRegistrationOnLockedFinancialPeriodActionApp: null,
    EditTimeRegistrationOnClosedSalaryPeriodActionApp: null,
    EditTimeRegistrationOnLockedFinancialPeriodActionApp: null,
    CreateTimeRegistrationOnClosedSalaryPeriodActionApp: null,
    UseExpenses: null,
    ProjectRequiredForExpenses: null,
    NoteRequiredForExpenses: null
  };

  const allAccountSettingsKeys = Object.keys(accountSettingsToFetch) as readonly (keyof AccountSettings)[];
  const accountSettingLastFetchedAt = new Map<keyof AccountSettings, number>();
  const inFlightByKey = new Map<keyof AccountSettings, Promise<void>>();

  async function fetchAndMergeAccountSettings(keysToFetch: readonly (keyof AccountSettings)[]) {
    const response = await cacheProviderAPI.fetchAccountSettings(keysToFetch);

    if (response && response.HasErrors === false) {
      const settings: Partial<Record<keyof AccountSettings, unknown>> = {};
      const fetchedAt = Date.now();

      response.Data.forEach((setting) => {
        settings[setting.Name] = setting.Value;
        accountSettingLastFetchedAt.set(setting.Name, fetchedAt);
      });

      if (_accountSettings.value === null) {
        _accountSettings.value = settings as AccountSettings;

        return;
      }

      _accountSettings.value = {
        ..._accountSettings.value,
        ...settings
      } as AccountSettings;
    }
  }

  // The Type assertions are necessary here if we want typed accountSettings. I doubt there is a non-crazy way to avoid it.
  async function getAccountSettings(keysToFetch: readonly (keyof AccountSettings)[] = allAccountSettingsKeys) {
    const uniqueKeys = [...new Set(keysToFetch)];

    if (uniqueKeys.length === 0) {
      return;
    }

    if (_accountSettings.value === null && uniqueKeys.length !== allAccountSettingsKeys.length) {
      await fetchAndMergeAccountSettings(allAccountSettingsKeys);

      return;
    }

    await fetchAndMergeAccountSettings(uniqueKeys);
  }

  function getStaleKeys(keys: readonly (keyof AccountSettings)[], maxAgeMs: number) {
    const now = Date.now();

    return keys.filter((key) => {
      const lastFetchedAt = accountSettingLastFetchedAt.get(key);

      return lastFetchedAt === undefined || now - lastFetchedAt >= maxAgeMs;
    });
  }

  async function refreshAccountSettings(
    keysToRefresh: readonly (keyof AccountSettings)[],
    maxAgeMs = DEFAULT_ACCOUNT_SETTINGS_MAX_AGE_MS
  ) {
    if (_accountSettings.value === null) {
      await getAccountSettings();

      return;
    }

    const uniqueKeys = [...new Set(keysToRefresh)];

    if (uniqueKeys.length === 0) {
      return;
    }

    const staleKeys = getStaleKeys(uniqueKeys, maxAgeMs);

    if (staleKeys.length === 0) {
      return;
    }

    const inFlightPromises = staleKeys
      .map((key) => inFlightByKey.get(key))
      .filter((promise): promise is Promise<void> => promise !== undefined);

    const keysToRequest = staleKeys.filter((key) => !inFlightByKey.has(key));

    if (keysToRequest.length === 0) {
      await Promise.all(inFlightPromises);

      return;
    }

    const requestPromise = fetchAndMergeAccountSettings(keysToRequest);

    keysToRequest.forEach((key) => inFlightByKey.set(key, requestPromise));

    try {
      await Promise.all([...inFlightPromises, requestPromise]);
    } finally {
      keysToRequest.forEach((key) => inFlightByKey.delete(key));
    }
  }

  const _accountSettings = ref<AccountSettings | null>(null);

  const accountSettings = computed<AccountSettings>(() => {
    if (_accountSettings.value === null) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.AccountSettings, message: 'Account settings are not loaded yet' });
    }

    return readonly(_accountSettings.value);
  });

  return {
    _accountSettings,
    accountSettings,
    getAccountSettings,
    refreshAccountSettings
  };
});
